/**
 * Project Download Utilities
 * Parses AI responses containing project structures and generates downloadable files
 */

import JSZip from "jszip";

/**
 * Detect if a message contains a project structure
 */
export const isProjectStructure = (content) => {
  const lowerContent = content.toLowerCase();
  const projectIndicators = [
    "project structure",
    "folder structure",
    "directory structure",
    "file structure",
    "```typescript:",
    "```javascript:",
    "```python:",
    "```java:",
    "```go:",
    "```rust:",
    "```cpp:",
    "```c:",
    "```html:",
    "```css:",
    "```json:",
    "```yaml:",
    "```xml:",
    "```bash:",
    "```shell:",
    "```dockerfile:",
    "```docker:",
    "package.json",
    "requirements.txt",
    "pom.xml",
    "build.gradle",
    "cargo.toml",
    "go.mod",
    "composer.json",
    "gemfile",
  ];

  return projectIndicators.some((indicator) => lowerContent.includes(indicator));
};

/**
 * Parse code blocks with file paths from markdown
 * Format: ```language:path/to/file.ext
 */
export const parseCodeBlocks = (content) => {
  const codeBlockRegex = /```(\w+)?:?([^\n]+)?\n([\s\S]*?)```/g;
  const files = [];
  let match;

  while ((match = codeBlockRegex.exec(content)) !== null) {
    const [, language, filePath, code] = match;
    if (filePath && code) {
      files.push({
        path: filePath.trim(),
        language: language || "text",
        content: code.trim(),
      });
    }
  }

  // Also parse standard code blocks and try to infer file paths from context
  const standardCodeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
  const context = content.toLowerCase();
  let standardMatch;
  let blockIndex = 0;

  while ((standardMatch = standardCodeBlockRegex.exec(content)) !== null) {
    const [, language, code] = standardMatch;
    const blockContent = code.trim();

    // Try to infer file path from content or context
    let inferredPath = null;

    if (blockContent.includes("package.json") || blockContent.includes('"name"')) {
      inferredPath = "package.json";
    } else if (blockContent.includes("requirements.txt") || blockContent.includes("==")) {
      inferredPath = "requirements.txt";
    } else if (blockContent.includes("README") || blockContent.includes("# ")) {
      inferredPath = "README.md";
    } else if (blockContent.includes("dockerfile") || blockContent.includes("FROM ")) {
      inferredPath = "Dockerfile";
    } else if (blockContent.includes("tsconfig") || blockContent.includes('"compilerOptions"')) {
      inferredPath = "tsconfig.json";
    } else if (blockContent.includes("webpack") || blockContent.includes("module.exports")) {
      inferredPath = "webpack.config.js";
    } else if (language === "json" && !inferredPath) {
      inferredPath = `config${blockIndex}.json`;
    } else if (language === "javascript" || language === "js") {
      inferredPath = `index${blockIndex > 0 ? blockIndex : ""}.js`;
    } else if (language === "typescript" || language === "ts") {
      inferredPath = `index${blockIndex > 0 ? blockIndex : ""}.ts`;
    } else if (language === "python" || language === "py") {
      inferredPath = `main${blockIndex > 0 ? blockIndex : ""}.py`;
    } else if (language === "html") {
      inferredPath = `index${blockIndex > 0 ? blockIndex : ""}.html`;
    } else if (language === "css") {
      inferredPath = `style${blockIndex > 0 ? blockIndex : ""}.css`;
    }

    if (inferredPath && !files.find((f) => f.path === inferredPath)) {
      files.push({
        path: inferredPath,
        language: language || "text",
        content: blockContent,
      });
      blockIndex++;
    }
  }

  return files;
};

/**
 * Parse folder structure from markdown tree format
 */
export const parseFolderStructure = (content) => {
  const treeRegex = /```\n?([\s\S]*?)\n?```/;
  const match = content.match(treeRegex);
  if (!match) return [];

  const treeContent = match[1];
  const lines = treeContent.split("\n");
  const structure = [];

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) return;

    // Match tree structure patterns like: ├──, │, └──, etc.
    const pathMatch = trimmed.match(/(?:├──|└──|│\s+)(.+)/);
    if (pathMatch) {
      structure.push(pathMatch[1].trim());
    } else if (trimmed && !trimmed.startsWith("```")) {
      // Also capture simple file/folder names
      structure.push(trimmed);
    }
  });

  return structure;
};

/**
 * Generate project files from AI response
 */
export const generateProjectFiles = (content) => {
  const files = parseCodeBlocks(content);
  const folderStructure = parseFolderStructure(content);

  // If we have code blocks with paths, use those
  if (files.length > 0) {
    return files;
  }

  // Otherwise, try to create files from folder structure
  if (folderStructure.length > 0) {
    return folderStructure.map((path) => ({
      path,
      language: "text",
      content: `# ${path}\n\nThis file was generated from the project structure.`,
    }));
  }

  return [];
};

/**
 * Create and download project as ZIP
 */
export const downloadProjectAsZip = async (content, projectName = "project") => {
  try {
    const files = generateProjectFiles(content);

    if (files.length === 0) {
      throw new Error("No project files found in the response");
    }

    const zip = new JSZip();

    // Add all files to zip
    files.forEach((file) => {
      zip.file(file.path, file.content);
    });

    // Generate ZIP file
    const zipBlob = await zip.generateAsync({ type: "blob" });

    // Download
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${projectName.replace(/[^a-z0-9]/gi, "_")}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error generating project ZIP:", error);
    throw error;
  }
};

/**
 * Extract project name from content
 */
export const extractProjectName = (content) => {
  const nameMatch = content.match(/(?:project|app|application)[\s:]+([^\n]+)/i);
  if (nameMatch) {
    return nameMatch[1].trim().replace(/[^\w\s-]/g, "");
  }

  const titleMatch = content.match(/#\s+([^\n]+)/);
  if (titleMatch) {
    return titleMatch[1].trim().replace(/[^\w\s-]/g, "");
  }

  return "project";
};







